import { nanoid } from "nanoid";
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

import {
  SELECTION_VIEW_MORE_TYPES,
  SELECTION_WIDGET_TYPES,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useContentItems } from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  ContentItemType,
  ProductItem,
  WidgetItem,
  WidgetType,
} from "@is/types";

type Props = {
  widget: WidgetItem;
  contentItemId?: string;
  onShowAll?: (id: string) => void;
  onShowMore?: (id: string) => void;
  isBasketWidget?: boolean;
};

const COLLAPSED_WIDGET_ITEMS_LIMIT = 4;
const EXPANDED_WIDGET_ITEMS_LIMIT = 12;

const useManageWidgetItem = ({
  widget,
  contentItemId,
  onShowAll,
  onShowMore,
  isBasketWidget,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductListing);
  const { handleOpenProductDetails } = useContentItems();
  const widgetRef = useRef(null);

  const {
    updateViewState,
    items: contentItems,
    quickAddToBasketItem,
    updateItem,
  } = useContext(ViewStateContext).content;

  // this value needed to detect if the widget is updated and rerun the animation
  const widgetId = useRef(widget.id);

  const items = useMemo(() => {
    if (!widget.isExpanded) {
      return widget?.items?.slice(0, COLLAPSED_WIDGET_ITEMS_LIMIT);
    }

    if (widget.showAllItems) {
      return widget?.items;
    }

    return widget?.items?.slice(0, EXPANDED_WIDGET_ITEMS_LIMIT);
  }, [widget]);

  const handleViewMore = useCallback(() => {
    if (widgetId.current !== widget.id) {
      widgetId.current = widget.id;
    }

    trackEvent({
      eventType: SELECTION_VIEW_MORE_TYPES[widget.type],
      data: {},
    });

    if (onShowMore) {
      onShowMore(widget.id);
      return;
    }

    if (!contentItemId) {
      const newContentItems = [...contentItems];

      const newContentItemId = nanoid();

      newContentItems.push({
        type: ContentItemType.WidgetItem,
        id: newContentItemId,
        content: {
          ...widget,
          isExpanded: true,
        },
      });

      updateViewState({
        items: newContentItems,
        showBasketDrawer: false,
        focusedItemId: newContentItemId,
      });
      return;
    }

    updateItem(contentItemId, {
      content: {
        ...widget,
        isExpanded: true,
      },
    });
  }, [
    contentItemId,
    widget,
    contentItems,
    updateItem,
    updateViewState,
    onShowMore,
  ]);

  const handleShowAllItems = useCallback(() => {
    if (widgetId.current !== widget.id) {
      widgetId.current = widget.id;
    }

    trackEvent({
      eventType: SELECTION_VIEW_MORE_TYPES[widget.type],
      data: {},
    });

    if (onShowAll) {
      onShowAll(widget.id);
      return;
    }

    if (!contentItemId) {
      return;
    }

    updateItem(contentItemId, {
      content: {
        ...widget,
        showAllItems: true,
      },
    });
  }, [widget, contentItemId, updateItem, onShowAll]);

  const getAnimationDelayIndex = useCallback(
    (itemIndex: number) => {
      let newValue = itemIndex;

      // if widget is updated, reset the animation delay index in order to rerun the animation
      const isWidgetUpdated = widgetId.current !== widget.id;

      if (!widget.isExpanded || isWidgetUpdated) {
        return newValue;
      }

      if (widget.isExpanded && !widget.showAllItems) {
        newValue = itemIndex - COLLAPSED_WIDGET_ITEMS_LIMIT;
      }

      if (widget.isExpanded && widget.showAllItems) {
        newValue = itemIndex - EXPANDED_WIDGET_ITEMS_LIMIT;
      }

      return newValue < 0 ? 0 : newValue;
    },
    [widget]
  );

  const handleItemClick = useCallback(
    (productItem: ProductItem) => {
      trackEvent({
        eventType: SELECTION_WIDGET_TYPES[widget.type],
        data: { variantId: productItem.id, styleId: productItem.styleId },
      });

      handleOpenProductDetails({
        productItem,
        type: ContentItemType.WidgetItem,
        parentItemId: contentItemId,
      });
    },
    [handleOpenProductDetails, widget, contentItemId]
  );

  const handleQuickAddRemoveItem = useCallback(() => {
    updateViewState({ quickAddToBasketItem: null });
  }, [updateViewState]);

  const handleQuickAdd = useCallback(
    (item: ProductItem) => {
      trackEvent({
        eventType: TrackingEventTypes.QuickAddToBasketOpen,
        data: { id: item.id, styleId: item.styleId },
      });

      updateViewState({ quickAddToBasketItem: item });
    },
    [updateViewState]
  );

  const handleQuickAddLayerClick = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const showViewMoreButton =
    !widget.isExpanded && widget.items?.length > COLLAPSED_WIDGET_ITEMS_LIMIT;

  const showViewAllButton =
    widget.isExpanded &&
    !widget.showAllItems &&
    widget.items.length > EXPANDED_WIDGET_ITEMS_LIMIT;

  const showNoItemsMessage = useMemo(
    () =>
      !showViewMoreButton &&
      !showViewAllButton &&
      items.length > 4 &&
      !isBasketWidget &&
      widget.type !== WidgetType.AllColors,
    [
      isBasketWidget,
      items.length,
      showViewAllButton,
      showViewMoreButton,
      widget,
    ]
  );

  useEffect(() => {
    if ((widget.isExpanded || widget.showAllItems) && widgetRef.current) {
      widgetRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      });
    }
  }, [widget.isExpanded, widget.showAllItems, widgetRef]);

  return {
    items,
    showViewMoreButton,
    showViewAllButton,
    t,
    handleViewMore,
    handleShowAllItems,
    handleItemClick,
    getAnimationDelayIndex,
    widgetRef,
    handleQuickAdd,
    quickAddToBasketItem,
    handleQuickAddRemoveItem,
    handleQuickAddLayerClick,
    showNoItemsMessage,
  };
};

export default useManageWidgetItem;
